import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Collapse,
  Tooltip,
  keyframes,
} from '@mui/material';
import { XIcon, ArrowClockwiseIcon } from '@phosphor-icons/react';
import { useState, useMemo } from 'react';
import usePublishEvent from '../../hooks/usePublishEvent';
import NoDataFallback from '../../components/common/NoDataFallback';
import PublishHistorySkeleton from '../../components/KnowledgeBase/PublishHistorySkeleton';

interface PublishHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  selectedStore: any;
}

// API data interface matching the response format
interface ApiEvent {
  id: number;
  stage: string;
  source_path: string | null;
  error_message: string | null;
  created_at: string;
  tenant_name: string;
  run_id: string;
  status: string;
  target_path: string | null;
  process_metadata: {
    ingested_at?: string;
    transformed_at?: string;
  } | null;
  updated_at: string | null;
}

// Interface for the grouped data to display in the UI
interface HistoryRow {
  id: number;
  runId: string;
  title: string;
  stage: string;
  startTime: string;
  endTime: string | null;
  status: string;
  details: ApiEvent[];
}

// Helper function to format date to a readable format
const formatToUTC = (dateString: string | null): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);

    // Format: "Feb 27, 2025 19:14:15"
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

// Helper function to remove s3://quibble-client/ prefix from paths
const removeS3Prefix = (path: string | null): string => {
  if (!path) return 'N/A';
  return path.replace('s3://quibble-client/', '');
};

// Helper function to get color based on status regardless of capitalization
const getStatusColor = (status: string) => {
  const normalizedStatus = status.toUpperCase();

  if (
    normalizedStatus.includes('COMPLETED') ||
    normalizedStatus.includes('COMPLETE')
  ) {
    return {
      bg: '#E8F5E9',
      text: '#2E7D32',
    };
  } else if (
    normalizedStatus.includes('FAILED') ||
    normalizedStatus.includes('FAIL')
  ) {
    return {
      bg: '#FFEBEE',
      text: '#C62828',
    };
  } else if (
    normalizedStatus.includes('IN_PROGRESS') ||
    normalizedStatus.includes('PROGRESS') ||
    normalizedStatus.includes('STARTED')
  ) {
    return {
      bg: '#FFF8E1',
      text: '#F57F17',
    };
  } else if (normalizedStatus.includes('PENDING')) {
    return {
      bg: '#E3F2FD',
      text: '#1565C0',
    };
  } else {
    // Default color for unknown status
    return {
      bg: '#E3F2FD',
      text: '#1565C0',
    };
  }
};

const Row = ({ row }: { row: HistoryRow }) => {
  const [open, setOpen] = useState(false);

  // Check if any event in this row has an error message
  const hasErrorMessage = row.details.some(detail => detail.error_message);

  // Get the colors for the status
  const statusColors = getStatusColor(row.status);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          borderBottom: '1px solid rgba(224, 224, 224, 1)',
          cursor: 'pointer',
        }}
        onClick={() => setOpen(!open)}
      >
        <Box
          sx={{
            fontSize: '14px',
            py: 2,
            px: 2,
            width: '20%',
            color: 'primary.main',
            fontWeight: 500,
            textDecoration: 'underline',
          }}
        >
          {row.runId}
        </Box>
        <Box
          sx={{
            fontSize: '14px',
            py: 2,
            px: 2,
            width: '20%',
            color: 'lightGray.text',
          }}
        >
          {row.title}
        </Box>
        <Box
          sx={{
            fontSize: '14px',
            py: 2,
            px: 2,
            width: '20%',
            color: 'lightGray.text',
          }}
        >
          {row.startTime}
        </Box>
        <Box
          sx={{
            fontSize: '14px',
            py: 2,
            px: 2,
            width: '20%',
            color: 'lightGray.text',
          }}
        >
          {row.endTime || 'N/A'}
        </Box>
        <Box
          sx={{
            fontSize: '14px',
            py: 2,
            px: 2,
            width: '20%',
            color: 'lightGray.text',
          }}
        >
          <Box
            sx={{
              bgcolor: statusColors.bg,
              color: statusColors.text,
              borderRadius: '4px',
              px: 1.5,
              py: 0.5,
              display: 'inline-block',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {row.status}
          </Box>
        </Box>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ py: 2, px: 2 }}>
          <Box
            sx={{
              border: '1px solid rgba(224, 224, 224, 1)',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                bgcolor: '#F5F9FC',
                borderBottom: '1px solid rgba(224, 224, 224, 1)',
              }}
            >
              <Box
                sx={{
                  width: hasErrorMessage ? '14.28%' : '16.67%',
                  fontSize: '14px',
                  fontWeight: 600,
                  py: 1,
                  px: 2,
                  color: 'custom.textBlue',
                }}
              >
                Stage
              </Box>
              <Box
                sx={{
                  width: hasErrorMessage ? '14.28%' : '16.67%',
                  fontSize: '14px',
                  fontWeight: 600,
                  py: 1,
                  px: 2,
                  color: 'custom.textBlue',
                }}
              >
                Status
              </Box>
              <Box
                sx={{
                  width: hasErrorMessage ? '14.28%' : '16.67%',
                  fontSize: '14px',
                  fontWeight: 600,
                  py: 1,
                  px: 2,
                  color: 'custom.textBlue',
                }}
              >
                Source Path
              </Box>
              <Box
                sx={{
                  width: hasErrorMessage ? '14.28%' : '16.67%',
                  fontSize: '14px',
                  fontWeight: 600,
                  py: 1,
                  px: 2,
                  color: 'custom.textBlue',
                }}
              >
                Target Path
              </Box>
              <Box
                sx={{
                  width: hasErrorMessage ? '14.28%' : '16.67%',
                  fontSize: '14px',
                  fontWeight: 600,
                  py: 1,
                  px: 2,
                  color: 'custom.textBlue',
                }}
              >
                Start Time
              </Box>
              <Box
                sx={{
                  width: hasErrorMessage ? '14.28%' : '16.67%',
                  fontSize: '14px',
                  fontWeight: 600,
                  py: 1,
                  px: 2,
                  color: 'custom.textBlue',
                }}
              >
                End Time
              </Box>
              {hasErrorMessage && (
                <Box
                  sx={{
                    width: '14.28%',
                    fontSize: '14px',
                    fontWeight: 600,
                    py: 1,
                    px: 2,
                    color: 'custom.textBlue',
                  }}
                >
                  Error Message
                </Box>
              )}
            </Box>
            {row.details?.map(detail => (
              <Box
                key={detail.id}
                sx={{
                  display: 'flex',
                  borderBottom: '1px solid rgba(224, 224, 224, 1)',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    width: hasErrorMessage ? '14.28%' : '16.67%',
                    fontSize: '12px',
                    py: 1,
                    px: 2,
                    color: 'lightGray.text',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  {detail.stage}
                </Box>
                <Box
                  sx={{
                    width: hasErrorMessage ? '14.28%' : '16.67%',
                    fontSize: '12px',
                    py: 1,
                    px: 2,
                    color: 'lightGray.text',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: getStatusColor(detail.status).bg,
                      color: getStatusColor(detail.status).text,
                      borderRadius: '4px',
                      px: 1.5,
                      py: 0.5,
                      display: 'inline-block',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    {detail.status}
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: hasErrorMessage ? '14.28%' : '16.67%',
                    fontSize: '12px',
                    py: 1,
                    px: 2,
                    color: 'lightGray.text',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  {removeS3Prefix(detail.source_path)}
                </Box>
                <Box
                  sx={{
                    width: hasErrorMessage ? '14.28%' : '16.67%',
                    fontSize: '12px',
                    py: 1,
                    px: 2,
                    color: 'lightGray.text',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  {removeS3Prefix(detail.target_path)}
                </Box>
                <Box
                  sx={{
                    width: hasErrorMessage ? '14.28%' : '16.67%',
                    fontSize: '12px',
                    py: 1,
                    px: 2,
                    color: 'lightGray.text',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  {formatToUTC(detail.created_at)}
                </Box>
                <Box
                  sx={{
                    width: hasErrorMessage ? '14.28%' : '16.67%',
                    fontSize: '12px',
                    py: 1,
                    px: 2,
                    color: 'lightGray.text',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  {formatToUTC(detail.updated_at)}
                </Box>
                {hasErrorMessage && (
                  <Box
                    sx={{
                      width: '14.28%',
                      fontSize: '12px',
                      py: 1,
                      px: 2,
                      color: detail.error_message
                        ? '#C62828'
                        : 'lightGray.text',
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      fontWeight: detail.error_message ? 500 : 400,
                    }}
                  >
                    {detail.error_message || 'None'}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Collapse>
    </>
  );
};

const PublishHistoryDrawer = ({
  open,
  onClose,
  selectedStore,
}: PublishHistoryDrawerProps) => {
  // Get the tenant name from useUser hook
  const { data, dataLoading, refetch } = usePublishEvent({
    selectedStore,
  });

  // Define rotation animation - update this to use theme.transitions
  const rotate = keyframes({
    from: {
      transform: 'rotate(0deg)',
    },
    to: {
      transform: 'rotate(360deg)',
    },
  });

  // Process the API data to show all events
  const processedData = useMemo(() => {
    if (!data?.data || !Array.isArray(data.data)) return [];

    // Group events by run_id
    const groupedByRunId: Record<string, ApiEvent[]> = {};
    data.data.forEach((event: ApiEvent) => {
      if (!groupedByRunId[event.run_id]) {
        groupedByRunId[event.run_id] = [];
      }
      groupedByRunId[event.run_id].push(event);
    });

    // Map each group to a HistoryRow
    const rows: HistoryRow[] = Object.entries(groupedByRunId).map(
      ([runId, events]) => {
        // Sort events by id to ensure correct order
        const sortedEvents = [...events].sort((a, b) => a.id - b.id);

        // Find stage 1 and stage 2 events
        const stage1Event = sortedEvents.find(
          event => event.stage === 'RECEIVED'
        );
        const stage2Event = sortedEvents.find(
          event => event.stage === 'TRANSFORMED'
        );

        // Find all stage 3 events
        const stage3Events = sortedEvents.filter(
          event => event.stage === 'INGESTED'
        );

        // Determine start time: Use stage 1's start time if available, otherwise use stage 2's start time
        let startTime = 'N/A';
        if (stage1Event) {
          startTime = formatToUTC(stage1Event.created_at);
        } else if (stage2Event) {
          startTime = formatToUTC(stage2Event.created_at);
        }

        // Determine end time: Use stage 3's updated_at time if available
        let endTime = null;
        if (stage3Events.length > 0) {
          // Find the stage 3 event with updated_at time
          const completedStage3Event = stage3Events.find(
            event => event.updated_at
          );
          if (completedStage3Event) {
            endTime = formatToUTC(completedStage3Event.updated_at);
          }
        }

        // Check if any event has an error message
        const hasError = sortedEvents.some(event => event.error_message);

        // Get the status - if any event has an error, set status to Failed
        let status;
        if (hasError) {
          status = 'Failed';
        } else {
          // Get the latest status based on the most recent event
          const latestEvent = sortedEvents[sortedEvents.length - 1];
          status = latestEvent.status;
        }

        // Use constant title for all rows
        const title = 'Data Injection to Quibble Schema';

        return {
          id: sortedEvents[sortedEvents.length - 1].id,
          runId,
          stage: sortedEvents[sortedEvents.length - 1].stage,
          title,
          startTime,
          endTime,
          status,
          details: sortedEvents,
        };
      }
    );

    // Sort by id descending to show newest events first
    return rows.sort((a, b) => b.id - a.id);
  }, [data]);

  // Handle refresh button click
  const handleRefresh = () => {
    refetch();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: '935px' },
      }}
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            px: 2,
            py: 1,
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: '18px',
              fontWeight: 600,
            }}
          >
            Menu Publish History
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip
              title={dataLoading ? 'Refreshing data...' : 'Refresh data'}
            >
              <span>
                <IconButton
                  onClick={dataLoading ? undefined : handleRefresh}
                  sx={{
                    mr: 1,
                    '&.MuiIconButton-root': {
                      animation: dataLoading
                        ? `${rotate} 1s linear infinite`
                        : 'none',
                    },
                    color: dataLoading ? 'primary.main' : 'inherit',
                    cursor: dataLoading ? 'default' : 'pointer',
                  }}
                  disabled={dataLoading}
                >
                  <ArrowClockwiseIcon size={20} />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={onClose}>
              <XIcon size={24} />
            </IconButton>
          </Box>
        </Box>
        {selectedStore && !selectedStore.is_menu_configured ? (
          <NoDataFallback
            message="No publish history available for this store."
            description="Please configure the menu for this store to see the publish history."
          />
        ) : dataLoading ? (
          <PublishHistorySkeleton />
        ) : (
          <Box
            sx={{
              border: '1px solid rgba(224, 224, 224, 1)',
              borderRadius: '8px',
              overflow: 'hidden',
              mx: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                bgcolor: '#F5F9FC',
                height: '40px',
                borderBottom: '1px solid rgba(224, 224, 224, 1)',
              }}
            >
              <Box
                sx={{
                  width: '20%',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'custom.textBlue',
                  py: 1,
                  px: 2,
                }}
              >
                Run ID
              </Box>
              <Box
                sx={{
                  width: '20%',
                  fontSize: '14px',
                  fontWeight: 600,
                  py: 1,
                  px: 2,
                }}
              >
                Title
              </Box>
              <Box
                sx={{
                  width: '20%',
                  fontSize: '14px',
                  fontWeight: 600,
                  py: 1,
                  px: 2,
                }}
              >
                Start Time
              </Box>
              <Box
                sx={{
                  width: '20%',
                  fontSize: '14px',
                  fontWeight: 600,
                  py: 1,
                  px: 2,
                }}
              >
                End Time
              </Box>
              <Box
                sx={{
                  width: '20%',
                  fontSize: '14px',
                  fontWeight: 600,
                  py: 1,
                  px: 2,
                }}
              >
                Status
              </Box>
            </Box>
            <Box>
              {processedData.length > 0 ? (
                processedData.map((row, index) => (
                  <Row key={`${row.id}-${index}`} row={row} />
                ))
              ) : (
                <NoDataFallback
                  message="No publish history available."
                  description="Please push the menu to this store to see the publish history."
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default PublishHistoryDrawer;
